import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';

import authAxios from '../../components/authAxios';
import { UserContext } from '../../App';

/* Single seam for the mobile applications list's data.

   Live: GET loans/loan-applications/?page_size={page_size} — the
   app-wide "whole list" size from UserContext (1000). The list action
   returns the slim LoanApplicationListSerializer rows (no nested
   financials), read from the paginated envelope's `results`.
   Filtering, search and sort all happen client-side in
   MobileApplications.

   The list is the pipeline, as on desktop: the backend excludes
   applications whose loan is disbursed or closed, so "Approved" means
   approved and not yet disbursed.

   `amount` is the amount applied. Display formatting (KES, term string,
   date, initials) happens in the component, not here.

   Own query key so it can't collide with the desktop list's paged
   ['applications', page, search] cache. */

const toApplication = (row) => ({
    id: row.id,
    reference: row.lc_reference,
    date: row.date,
    customerName: row.customer_name,
    loanType: row.product_name,
    amount: Number(row.amount),
    term: row.term,
    termUnitName: row.term_unit_name,
    // `status` is the code (filter, pill colour); `statusDisplay` the text.
    status: row.status,
    statusDisplay: row.status_name,
});

const countBy = (applications, status) =>
    applications.filter(application => application.status === status).length;

const useApplicationsData = () => {
    const { page_size } = useContext(UserContext);

    const { data: applications, isLoading, isError, refetch } = useQuery({
        queryKey: ['applications', 'mobile'],
        queryFn: async () => {
            const response = await authAxios.get(`/loans/loan-applications/?page_size=${page_size}`);
            return response.data.results.map(toApplication);
        },
        // Fail fast so the Retry state shows instead of a long skeleton.
        retry: 1,
    });

    const counts = applications && {
        all: applications.length,
        pending: countBy(applications, 'PENDING'),
        approved: countBy(applications, 'APPROVED'),
        declined: countBy(applications, 'DECLINED'),
        withdrawn: countBy(applications, 'WITHDRAWN'),
    };

    return { isLoading, isError, refetch, counts, applications: applications || [] };
};

export default useApplicationsData;
